import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Bell, MapPin, X, CheckCheck, Check, Trash2, FilePlus, Users, CheckCircle2, XCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { InFeedAd } from "@/components/AdBanner";

const STATUS_COLORS = {
  working: "#00FF94",
  out_of_service: "#FF3B30",
  out_of_cash: "#FFCC00",
  unknown: "#71717A",
};

const STATUS_LABELS = {
  working: "Working",
  out_of_service: "Out of Service",
  out_of_cash: "Out of Cash",
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function useUnreadCount(user) {
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!user) { setCount(0); return; }
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setCount(res.data.count);
    } catch {
      // silently fail
    }
  }, [user]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  return { count, refresh: fetchCount };
}

export default function NotificationsPane({ open, onOpenChange, onRead }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleNotificationTap = (n) => {
    // Mark read first (fire-and-forget) so the bell badge clears immediately.
    if (!n.read) markOneRead(n.id);

    // Compute the deep-link destination per type. Default (atm_status /
    // unknown): no navigation — the bell row already shows everything.
    let dest = null;
    if (n.type === "atm_submission") {
      // Admin-only flow: open Admin → ATM Submissions tab.
      dest = "/admin?tab=submissions";
    } else if (n.type === "submission_approved") {
      // The submitter's ATM is now live — deep-link to its pin on the map.
      // Map lives at `/` (and `/dashboard`), NOT `/map`. Sending users to
      // `/map?atm=…` would land them on a no-matching-route black screen.
      const atmId = n.atm_id || n.metadata?.atm_id;
      dest = atmId ? `/?atm=${atmId}` : "/";
    } else if (n.type === "submission_rejected") {
      // No live ATM to deep-link to — open the user's own submissions list
      // so they can see status + admin feedback for everything they've sent.
      dest = "/my-submissions";
    } else if (n.type === "circle_invite") {
      // Open Trusted Network with the invites pane preselected so the user
      // lands directly on the row they need to act on.
      dest = "/trusted-network?invites=1";
    } else if (n.type === "trusted_circle") {
      // Trusted-circle notifications carry an `atm_id` (legacy flat field
      // mirrors `metadata.atm_id`). Open the map deep-linked to that ATM.
      const atmId = n.atm_id || n.metadata?.atm_id;
      dest = atmId ? `/?atm=${atmId}` : "/";
    }

    // Close the bell tray, then navigate on the next tick. The brief delay
    // gives Radix's Sheet a frame to clean up its overlay before the next
    // route renders underneath it.
    onOpenChange?.(false);
    if (dest) {
      setTimeout(() => navigate(dest), 50);
    }
  };

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      onRead?.();
    } catch {
      // silently fail
    }
  };

  const markOneRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => n.id === notifId ? { ...n, read: true } : n));
      onRead?.();
    } catch {
      // silently fail
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/notifications`, { withCredentials: true });
      setNotifications([]);
      onRead?.();
    } catch {
      // silently fail
    }
  };

  const deleteOne = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}`, { withCredentials: true });
      setNotifications((prev) => prev.filter((n) => n.id !== notifId));
      onRead?.();
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const unread = notifications.filter((n) => !n.read).length;

  // Unread / All tabs. Default to Unread if there's anything to act on, else
  // All. Tracked here (not in URL) so it doesn't bleed across other components.
  // We also auto-flip the user to All after they hit "Read all" so they
  // don't sit on an empty Unread tab.
  const [activeTab, setActiveTab] = useState("all");
  // When the pane opens, pick a sensible default tab. We only re-evaluate
  // on `open` flipping true so the tab choice doesn't jump under the user
  // mid-session.
  useEffect(() => {
    if (open) {
      setActiveTab(notifications.some((n) => !n.read) ? "unread" : "all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const visibleNotifications = activeTab === "unread"
    ? notifications.filter((n) => !n.read)
    : notifications;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#09090B] border-zinc-800 w-[340px] sm:w-[400px] p-0" data-testid="notifications-pane" aria-describedby={undefined}>
        <SheetHeader className="px-4 pt-5 pb-3 border-b border-zinc-800/50">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white font-heading text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#00FF94]" strokeWidth={1.5} />
              Notifications
              {unread > 0 && (
                <span className="bg-[#FF3B30] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="unread-badge-pane">
                  {unread}
                </span>
              )}
            </SheetTitle>
            {/* Right-side actions. `mr-8` reserves space for the SheetContent's
                built-in absolute-positioned close-X so the Read/Clear buttons
                don't crash into it. */}
            <div className="flex items-center gap-3 mr-8">
              {unread > 0 && (
                <button
                  onClick={() => { markAllRead(); setActiveTab("all"); }}
                  className="flex items-center gap-1 text-xs text-[#00FF94] hover:text-[#00FF94]/80 font-semibold transition-colors"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Read all
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="flex items-center gap-1 text-xs text-[#FF3B30] hover:text-[#FF3B30]/80 font-semibold transition-colors"
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Clear all
                </button>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Unread / All tab pills. Counts are live so the user always sees
            exactly how many actionable items remain. Active tab uses the
            brand green to match the "Read all" CTA above. */}
        <div className="px-4 pt-2.5 pb-2 border-b border-zinc-800/50 flex gap-2">
          <button
            onClick={() => setActiveTab("unread")}
            className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
              activeTab === "unread"
                ? "bg-[#00FF94] text-black"
                : "bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800"
            }`}
            data-testid="notif-tab-unread"
          >
            Unread ({unread})
          </button>
          <button
            onClick={() => setActiveTab("all")}
            className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
              activeTab === "all"
                ? "bg-[#00FF94] text-black"
                : "bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800"
            }`}
            data-testid="notif-tab-all"
          >
            All ({notifications.length})
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 130px)" }}>
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-5 h-5 border-2 border-[#00FF94] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="no-notifications">
              <Bell className="w-10 h-10 text-zinc-700 mb-3" strokeWidth={1.5} />
              <p className="text-sm text-zinc-500 font-medium">No notifications yet</p>
              <p className="text-xs text-zinc-600 mt-1">Watch ATMs to get status change alerts</p>
            </div>
          ) : visibleNotifications.length === 0 ? (
            // Inbox-zero state for the Unread tab. Lands as a small win when
            // the user has caught up on everything actionable.
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="no-unread-notifications">
              <CheckCheck className="w-10 h-10 text-[#00FF94] mb-3" strokeWidth={1.5} />
              <p className="text-sm text-zinc-300 font-medium">You're all caught up</p>
              <p className="text-xs text-zinc-500 mt-1">No unread notifications. Tap All to see your history.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {visibleNotifications.map((n, idx) => (
                <React.Fragment key={n.id}>
                <NotificationRow
                  n={n}
                  onTap={handleNotificationTap}
                  onMarkRead={markOneRead}
                  onDelete={deleteOne}
                />
                {/* In-feed ad every 5 notifications. Free-tier only;
                    Pro users see nothing. Indexed off `visibleNotifications`
                    so tab switches don't leave orphaned ad rows. */}
                {idx > 0 && (idx + 1) % 5 === 0 && idx < visibleNotifications.length - 1 && (
                  <InFeedAd className="my-1" testId={`notif-infeed-ad-${idx}`} />
                )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * One notification row — dispatches layout on `n.type`:
 *   - "atm_status" (default / legacy) → status badge + ATM name + address
 *   - "atm_submission" (admins only)  → submission icon + bank/address
 *   - "circle_invite" (recipient)     → users icon + inviter/circle line
 *
 * The whole row is a clickable button for tap-to-navigate. The trailing
 * mark-read / delete icon buttons live on top of it and stop propagation so
 * tapping them doesn't also trigger the deep-link nav.
 */
function NotificationRow({ n, onTap, onMarkRead, onDelete }) {
  const type = n.type || "atm_status";
  const isStatus = type === "atm_status";
  const isSubmission = type === "atm_submission";
  const isSubmissionApproved = type === "submission_approved";
  const isSubmissionRejected = type === "submission_rejected";
  const isInvite = type === "circle_invite";
  const isTrustedCircle = type === "trusted_circle";

  return (
    <button
      type="button"
      onClick={() => onTap(n)}
      className={`w-full text-left px-4 py-3 transition-colors hover:bg-zinc-900/40 ${!n.read ? "bg-[#00FF94]/[0.03]" : ""}`}
      data-testid={`notification-${n.id}`}
    >
      <div className="flex gap-3">
        <div
          className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
          style={{ background: !n.read ? "#00FF94" : "transparent" }}
        />
        <div className="min-w-0 flex-1">
          {isStatus && (
            <>
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: STATUS_COLORS[n.status] || STATUS_COLORS.unknown }}
                />
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: STATUS_COLORS[n.status] || STATUS_COLORS.unknown }}>
                  {STATUS_LABELS[n.status] || n.status}
                </span>
              </div>
              <p className="text-sm text-white font-medium break-words leading-snug">{n.atm_name}</p>
              <p className="text-xs text-zinc-500 flex items-start gap-1 mt-0.5 leading-snug">
                <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="break-words min-w-0">{n.atm_address}</span>
              </p>
            </>
          )}
          {isSubmission && (
            <>
              <div className="flex items-center gap-2 mb-0.5">
                <FilePlus className="w-3 h-3 text-[#00FF94] flex-shrink-0" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-wide text-[#00FF94]">New Submission</span>
              </div>
              <p className="text-sm text-white font-medium break-words leading-snug">{n.atm_name}</p>
              {n.atm_address && (
                <p className="text-xs text-zinc-500 flex items-start gap-1 mt-0.5 leading-snug">
                  <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span className="break-words min-w-0">{n.atm_address}</span>
                </p>
              )}
              {n.message && (
                <p className="text-[11px] text-zinc-400 mt-1 break-words leading-snug">{n.message}</p>
              )}
            </>
          )}
          {isSubmissionApproved && (() => {
            const meta = n.metadata || {};
            const bank = meta.bank_name || n.atm_name || "Your ATM";
            const addr = meta.atm_address || n.atm_address || "";
            return (
              <>
                <div className="flex items-center gap-2 mb-0.5">
                  <CheckCircle2 className="w-3 h-3 text-[#00FF94] flex-shrink-0" strokeWidth={2} />
                  <span className="text-xs font-bold uppercase tracking-wide text-[#00FF94]">Submission Approved</span>
                </div>
                <p className="text-sm text-white font-medium break-words leading-snug">
                  Your <span className="text-[#00FF94]">{bank}</span> submission is live
                </p>
                {addr && (
                  <p className="text-xs text-zinc-500 flex items-start gap-1 mt-0.5 leading-snug">
                    <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="break-words min-w-0">{addr}</span>
                  </p>
                )}
                <p className="text-[11px] text-zinc-400 mt-1 break-words leading-snug">
                  Tap to view it on the map. Thanks for contributing!
                </p>
              </>
            );
          })()}
          {isSubmissionRejected && (() => {
            const meta = n.metadata || {};
            const bank = meta.bank_name || n.atm_name || "Your ATM";
            const addr = meta.atm_address || n.atm_address || "";
            const feedback = meta.feedback || "";
            return (
              <>
                <div className="flex items-center gap-2 mb-0.5">
                  <XCircle className="w-3 h-3 text-[#FF3B30] flex-shrink-0" strokeWidth={2} />
                  <span className="text-xs font-bold uppercase tracking-wide text-[#FF3B30]">Submission Rejected</span>
                </div>
                <p className="text-sm text-white font-medium break-words leading-snug">
                  Your <span className="text-[#FF3B30]">{bank}</span> submission was not approved
                </p>
                {addr && (
                  <p className="text-xs text-zinc-500 flex items-start gap-1 mt-0.5 leading-snug">
                    <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="break-words min-w-0">{addr}</span>
                  </p>
                )}
                {feedback && (
                  <p className="text-[11px] text-zinc-300 mt-1 break-words leading-snug">
                    <span className="text-[#FF3B30] font-bold uppercase tracking-wider text-[9px]">Admin note:</span> {feedback}
                  </p>
                )}
                <p className="text-[10px] text-zinc-500 mt-1">Tap to view all your submissions.</p>
              </>
            );
          })()}
          {isInvite && (
            <>
              <div className="flex items-center gap-2 mb-0.5">
                <Users className="w-3 h-3 text-[#FFCC00]" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-wide text-[#FFCC00]">Circle Invite</span>
              </div>
              <p className="text-sm text-white font-medium truncate">
                {n.inviter_name} invited you
              </p>
              <p className="text-xs text-zinc-400 truncate mt-0.5">
                Join <span className="text-white font-semibold">"{n.circle_name}"</span>
              </p>
            </>
          )}
          {isTrustedCircle && (() => {
            // Trusted-circle layout — multi-line so the body never clips. Pulls
            // from `metadata.*` written by the trusted/check-nearby flow so it
            // doesn't depend on the legacy flat `atm_name` / `atm_address`
            // fields (those exist for tray back-compat, but `metadata` is the
            // primary source of truth for trusted notifications).
            const meta = n.metadata || {};
            const reporter = meta.reporter_name || n.reporter_name || "Someone";
            const bank = meta.bank_name || n.atm_name || "ATM";
            const addr = meta.atm_address || n.atm_address || "";
            const status = meta.status || n.status || "unknown";
            const statusLabel = STATUS_LABELS[status] || status;
            const statusColor = STATUS_COLORS[status] || STATUS_COLORS.unknown;
            const circleNames = Array.isArray(meta.triggered_circle_names)
              ? meta.triggered_circle_names
              : (Array.isArray(meta.circle_names) ? meta.circle_names : []);
            return (
              <>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Users className="w-3 h-3 text-[#00FF94] flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#00FF94]">Trusted Circle</span>
                  {circleNames.length > 0 && (
                    <span className="text-[10px] text-zinc-500">· {circleNames.join(", ")}</span>
                  )}
                </div>
                <p className="text-sm text-white font-medium leading-snug">
                  {reporter} reported <span className="text-zinc-300">{bank}</span>
                </p>
                {addr && (
                  <p className="text-xs text-zinc-500 flex items-start gap-1 mt-0.5 leading-snug">
                    <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="break-words">{addr}</span>
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span
                    className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: statusColor }}
                  />
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: statusColor }}>
                    {statusLabel}
                  </span>
                  {/* Phase A: capability pill so the recipient sees at a
                      glance whether the report is about withdrawals or
                      deposits. Legacy notifications without the field
                      default to Withdrawal so we don't drop the pill. */}
                  <span
                    className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300"
                    data-testid={`notif-txn-${(meta.transaction_type || "withdraw")}`}
                  >
                    {(meta.transaction_type || "withdraw") === "deposit" ? "Deposit" : "Withdraw"}
                  </span>
                  {meta.line_status && meta.line_status !== "unknown" && (
                    <span className="text-[10px] text-zinc-500">
                      · {meta.line_status === "no_line" ? "no line" : meta.line_status === "short_line" ? "short line" : "long line"}
                    </span>
                  )}
                </div>
              </>
            );
          })()}
          {/* Fallback for any future type — show the raw message. */}
          {!isStatus && !isSubmission && !isSubmissionApproved && !isSubmissionRejected && !isInvite && !isTrustedCircle && (
            <p className="text-sm text-white truncate">{n.message || "Notification"}</p>
          )}
          <p className="text-[10px] text-zinc-600 mt-1">{timeAgo(n.created_at)}</p>
        </div>
        <div className="flex flex-col items-center gap-1 flex-shrink-0 self-start mt-1">
          {!n.read && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onMarkRead(n.id); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onMarkRead(n.id); } }}
              className="p-1 rounded-md text-zinc-600 hover:text-[#00FF94] hover:bg-[#00FF94]/10 transition-colors cursor-pointer"
              title="Mark as read"
              data-testid={`read-${n.id}`}
            >
              <Check className="w-3.5 h-3.5" strokeWidth={2} />
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onDelete(n.id); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onDelete(n.id); } }}
            className="p-1 rounded-md text-zinc-600 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors cursor-pointer"
            title="Delete notification"
            data-testid={`delete-${n.id}`}
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        </div>
      </div>
    </button>
  );
}
